import { screenCue } from "./audio.ts";
import type { Route } from "./router.ts";

/** The boards (the tabs, Upgrades included): a tab switch lands still, it never slides. */
export const BOARD_ROUTES: ReadonlySet<Route["name"]> = new Set<Route["name"]>(["open", "done", "schedules", "upgrades"]);

/**
 * How the screen now showing arrived (src/app.tsx useSlide; docs/feel.md). "push" is a move deeper
 * and slides in from the right. "back" is a return: a pop through history (an entry stamped lower
 * than the one just left: the Back link, the app's back swipe, Android's Back, the browser's back),
 * the back link to the screen it sits on, or any screen landing on a tab; the same rule as the
 * falling cue (src/audio.ts screenCue), so what is heard and what is seen agree. A returning screen
 * was there all along: it does not slide (owner, 2026-10-04: "When doing a native back swipe to go
 * back, it still plays the slide in animation. This looks weird."), and nothing inside it rises.
 * null: a tab switch, or the first screen after a load.
 */
export type Slide = "push" | "back" | null;

/** A screen's place in history: its hash and its entry's stamp (src/audio.ts), null before it has one. */
export interface Entry {
  hash: string;
  seq: number | null;
}

/** The arrival of `to` (named `name`) from `from`, the screen shown before it (null at launch). */
export function arrivalSlide(from: Entry | null, to: Entry, name: Route["name"]): Slide {
  if (!from) return null;
  const pop = to.seq !== null && from.seq !== null && to.seq < from.seq;
  if (screenCue(from.hash, to.hash, pop) === "close") return "back";
  return BOARD_ROUTES.has(name) ? null : "push";
}

/** Whether an arrival moves on screen: only a push. A return lands still (and so does reduced motion, src/app.tsx). */
export function slideMoves(slide: Slide): slide is "push" {
  return slide === "push";
}
